import { rawTimeZones, timeZonesNames } from '@vvo/tzdb'

interface ITimezoneCity {
  name: string
  country: string
}
export interface ITimezoneChoice {
  offset: number
  tzName: string
  cities: ITimezoneCity[]
}

// Reuse the installed geographical catalogue; compute current offsets with the browser's tz rules.
const { tzRawData } = require('timezone-select-js') as {
  tzRawData: Array<{ name: string; country: string; status: string }>
}

/** Compute the current UTC offset (hours) for an IANA timezone name. Returns null for unknown zones. */
export function getTzOffset(tzName: string | null): number | null {
  if (!tzName) return null
  try {
    const now = new Date()
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tzName,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    } as Intl.DateTimeFormatOptions).formatToParts(now)
    const value = (type: string) => Number(parts.find((part) => part.type === type)?.value)
    return (
      Math.round(
        (Date.UTC(value('year'), value('month') - 1, value('day'), value('hour') % 24, value('minute')) -
          Math.floor(now.getTime() / 60000) * 60000) /
          60000
      ) / 60
    )
  } catch {
    return null
  }
}

export function timezoneChoices(locale = 'en'): ITimezoneChoice[] {
  const countries =
    typeof (Intl as any).DisplayNames === 'function'
      ? new (Intl as any).DisplayNames([locale], { type: 'region' })
      : undefined
  const catalogue = new Map(tzRawData.map((zone) => [zone.name, zone.country]))
  // The legacy catalogue also contains fixed-offset aliases. Supplement it with
  // the current IANA catalogue used by Mobile, including newly split/renamed zones.
  rawTimeZones.forEach((zone) => {
    ;[zone.name, ...zone.group].forEach((name) => {
      if (!catalogue.get(name)) catalogue.set(name, zone.countryCode)
    })
  })
  timeZonesNames.forEach((name) => {
    if (!catalogue.has(name)) catalogue.set(name, '')
  })
  catalogue.set('UTC', '')
  const choices = new Map<string, ITimezoneChoice>()
  catalogue.forEach((countryCode, name) => {
    const zone = { name, country: countryCode }

    const offset = getTzOffset(zone.name)
    if (offset == null) return
    const code = zone.country.split(',')[0]
    const country = countries && /^[A-Z]{2}$/.test(code) ? countries.of(code) : code
    choices.set(zone.name, {
      offset,
      tzName: zone.name,
      cities: [{ name: zone.name.split('/').pop()!.replace(/_/g, ' '), country: country || '' }],
    })
  })
  return Array.from(choices.values()).sort((a, b) => a.offset - b.offset || a.tzName.localeCompare(b.tzName))
}
export const normalizeSearch = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/gmt/g, 'utc')
    .replace(/[−–]/g, '-')
    .replace(/[\s_/]/g, '')
